import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KeralaMap from "react-kerala-map";
import "react-kerala-map/style.css";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { getAllUsers, geocodeAddressQuery } from "@/services/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  Building2,
  Compass,
  Download,
  Expand,
  Layers,
  Loader2,
  LocateFixed,
  MapPin,
  MapPinned,
  Minimize2,
  RefreshCw,
  Search,
  Target,
  Users,
  X,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Configuration                                                             */
/* -------------------------------------------------------------------------- */

// Same hosted dataset the library defaults to (CORS-enabled GitHub Pages).
// Override with REACT_APP_KERALA_MAP_DATA_URL if the data is self-hosted.
const KERALA_MAP_DATA_URL =
  process.env.REACT_APP_KERALA_MAP_DATA_URL ||
  "https://gvnair.github.io/Kerala_Representative_Map/data";

// Local body where Vruthi currently operates.
export const SERVICE_AREA = {
  district: "Ernakulam",
  code: "M07031",
  name: "Maradu",
  type: "Municipality",
};

const MARKER_STYLES = {
  service: {
    color: "#059669",
    label: "Maradu — service area",
    description: "Customer inside the Maradu Municipality",
  },
  district: {
    color: "#f59e0b",
    label: "Ernakulam — other local body",
    description: "Customer in Ernakulam but outside Maradu",
  },
  outside: {
    color: "#64748b",
    label: "Outside Ernakulam",
    description: "Customer outside the Ernakulam district",
  },
};

const FILTERS = [
  { value: "all", label: "All customers" },
  { value: "service", label: `In ${SERVICE_AREA.name}` },
  { value: "district", label: "Ernakulam (outside Maradu)" },
  { value: "outside", label: "Outside Ernakulam" },
];

const GEOCODE_CACHE_KEY = "vruthi.localMapAssessment.geocodeCache.v1";
const MAX_LOCAL_BODY_ROWS = 12;

// Drill straight into Ernakulam → Maradu on first load. The library loads the
// district's ward boundaries (~11 MB for Ernakulam) as part of that drill-down,
// so set this to false if the dashboard is used on slow connections.
const AUTO_FOCUS_SERVICE_AREA = true;

/* -------------------------------------------------------------------------- */
/*  GeoJSON helpers                                                           */
/* -------------------------------------------------------------------------- */

// Mirrors the library's `toDistrictFileName` (lowercase, spaces -> underscore).
const toDistrictFileName = (district) =>
  `${String(district).toLowerCase().replace(/\s+/g, "_")}.geojson`;

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180;

// Ray-casting point-in-ring test. Coordinates are GeoJSON ([lng, lat]) order.
const pointInRing = (lng, lat, ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const crosses = yi > lat !== yj > lat;
    if (crosses && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

// Polygon geometry is [outerRing, ...holes]; a point inside a hole is outside.
const polygonContains = (polygon, lng, lat) => {
  if (!polygon || polygon.length === 0) return false;
  if (!pointInRing(lng, lat, polygon[0])) return false;
  for (let i = 1; i < polygon.length; i++) {
    if (pointInRing(lng, lat, polygon[i])) return false;
  }
  return true;
};

const featureContains = (geometry, lat, lng) => {
  if (!geometry) return false;
  if (geometry.type === "Polygon") return polygonContains(geometry.coordinates, lng, lat);
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((polygon) => polygonContains(polygon, lng, lat));
  }
  return false;
};

const extendBBox = (bbox, lng, lat) => {
  if (!bbox) return [lng, lat, lng, lat];
  if (lng < bbox[0]) bbox[0] = lng;
  if (lat < bbox[1]) bbox[1] = lat;
  if (lng > bbox[2]) bbox[2] = lng;
  if (lat > bbox[3]) bbox[3] = lat;
  return bbox;
};

const getGeometryBBox = (geometry) => {
  if (!geometry) return null;
  let bbox = null;
  const walk = (node) => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === "number" && typeof node[1] === "number") {
      bbox = extendBBox(bbox, node[0], node[1]);
      return;
    }
    node.forEach(walk);
  };
  walk(geometry.coordinates);
  return bbox;
};

const bboxContains = (bbox, lat, lng) =>
  Boolean(bbox) && lng >= bbox[0] && lng <= bbox[2] && lat >= bbox[1] && lat <= bbox[3];

// Index of local body / ward polygons for fast repeated point-in-polygon lookups.
const buildDivisionIndex = (geojson) => {
  const features = (geojson && geojson.features) || [];
  const index = [];
  features.forEach((feature) => {
    const props = (feature && feature.properties) || {};
    const code = props.sec_kerala_code ? String(props.sec_kerala_code) : "";
    const bbox = getGeometryBBox(feature && feature.geometry);
    if (!code || !bbox) return;
    index.push({
      code,
      name: props.lsgd_name || "Unknown",
      type: props.lsgd_type || "Unclassified",
      wardNumber: props.ward_number,
      wardName: props.ward_name,
      geometry: feature.geometry,
      bbox,
    });
  });
  return index;
};

const findDivisionForPoint = (index, lat, lng) => {
  for (let i = 0; i < index.length; i++) {
    const division = index[i];
    if (!bboxContains(division.bbox, lat, lng)) continue;
    if (featureContains(division.geometry, lat, lng)) return division;
  }
  return null;
};

/* -------------------------------------------------------------------------- */
/*  Customer coordinate resolution                                            */
/* -------------------------------------------------------------------------- */

const parseCoordinateString = (value) => {
  if (!value || typeof value !== "string") return null;
  const parts = value.split(",").map((part) => parseFloat(part.trim()));
  if (parts.length === 2 && isValidLatLng(parts[0], parts[1])) return [parts[0], parts[1]];
  return null;
};

// Same precedence the Route Planning Map uses: verified `location` object,
// then the backend's cached coords, then numeric `location_string`.
const resolveStoredCoords = (user) => {
  const location = user.location || {};
  const lat = Number(location.lat ?? location.latitude);
  const lng = Number(location.lng ?? location.longitude);
  if (isValidLatLng(lat, lng)) return { coords: [lat, lng], source: "verified location" };

  const cached = user.map_coords;
  if (Array.isArray(cached) && cached.length === 2) {
    const cachedLat = Number(cached[0]);
    const cachedLng = Number(cached[1]);
    if (isValidLatLng(cachedLat, cachedLng)) {
      return { coords: [cachedLat, cachedLng], source: "cached map location" };
    }
  }

  const fromString = parseCoordinateString(user.location_string);
  if (fromString) return { coords: fromString, source: "location_string coordinates" };

  return { coords: null, source: null };
};

// Best string to send to the geocoder when a customer has no coordinates yet.
const getGeocodeCandidate = (user) => {
  if (user.location_string && String(user.location_string).trim() !== "") {
    return String(user.location_string).trim();
  }
  if (user.address && String(user.address).trim() !== "") {
    return String(user.address).trim();
  }
  if (user.pincode) return `${user.pincode}, Kerala, India`;
  return null;
};

const readGeocodeCache = () => {
  try {
    return JSON.parse(window.localStorage.getItem(GEOCODE_CACHE_KEY)) || {};
  } catch (err) {
    return {};
  }
};

const writeGeocodeCache = (cache) => {
  try {
    window.localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    // Storage full / disabled - geocoding still works, it just isn't cached.
  }
};

// Stable, tiny offset so customers sharing one address string don't stack up.
const withStableJitter = (coords, seed) => {
  const hash = String(seed || "")
    .split("")
    .reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 100000, 7);
  const first = ((hash % 100) / 100 - 0.5) * 0.0004;
  const second = (((hash >> 2) % 100) / 100 - 0.5) * 0.0004;
  return [coords[0] + first, coords[1] + second];
};

// ---------------------------------------------------------------------------
// Customer pins are plain DOM, NOT Leaflet layers, on purpose.
//
// react-kerala-map bundles its own private copy of Leaflet inside dist/ (it has
// no `leaflet` import/environment dependency), while this app also depends on
// the `leaflet` package. Creating an `L.circleMarker` with the app's copy and
// adding it to the library's map mixes two Leaflet instances: the app's Bounds
// object is not `instanceof` the library copy's Bounds, so its `toBounds()`
// re-wraps it into an empty Bounds and `intersects()` then throws
// "Cannot read properties of undefined (reading 'x')" on every zoom/move.
//
// So we only ever hand primitives (arrays of lat/lng, numbers) to the library's
// map API and draw the pins ourselves inside its markerPane. If the library is
// changed to treat `leaflet` as an external/peer dependency, this can go back to
// real L.circleMarker layers.
// ---------------------------------------------------------------------------
const PIN_CONTAINER_CLASSNAME = "vruthi-customer-pins";
const PIN_SIZE = 14; // px, outer pin diameter

// Plain-data bounds accumulator (copy-agnostic; no Leaflet Bounds object).
const extendPinBounds = (bounds, lat, lng) => {
  if (!bounds) return { minLat: lat, maxLat: lat, minLng: lng, maxLng: lng };
  if (lat < bounds.minLat) bounds.minLat = lat;
  if (lat > bounds.maxLat) bounds.maxLat = lat;
  if (lng < bounds.minLng) bounds.minLng = lng;
  if (lng > bounds.maxLng) bounds.maxLng = lng;
  return bounds;
};

const getCustomerKey = (user) =>
  user._id !== undefined && user._id !== null ? String(user._id) : String(user.id || user.email || user.name);

// GeoJSON files are large (Ernakulam local bodies ~4.5 MB, wards ~10.8 MB), so
// keep them cached for the lifetime of the tab.
const geoJsonCache = new Map();

const fetchGeoJson = async (url) => {
  if (geoJsonCache.has(url)) return geoJsonCache.get(url);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  const json = await response.json();
  geoJsonCache.set(url, json);
  return json;
};

const LOCAL_BODIES_URL = `${KERALA_MAP_DATA_URL}/kerala_lsgi/localbodies/${toDistrictFileName(
  SERVICE_AREA.district
)}`;
const WARDS_URL = `${KERALA_MAP_DATA_URL}/kerala_lsgi/wards/${toDistrictFileName(
  SERVICE_AREA.district
)}`;

/* -------------------------------------------------------------------------- */
/*  DOM-based marker helpers                                                  */
/* -------------------------------------------------------------------------- */

// Reposition all pin DOM elements on the map. We only read .x/.y from the
// library's Leaflet Point (primitive numbers) — never pass the Point across
// the library/app copy boundary.
const repositionPins = (map, container, pins) => {
  if (!map || !container || !pins.length) return;
  pins.forEach((pin) => {
    const coords = pin._vruthiCoords;
    if (!coords) return;
    const pt = map.latLngToLayerPoint([coords[0], coords[1]]);
    if (!pt) return;
    pin.style.left = `${pt.x}px`;
    pin.style.top = `${pt.y}px`;
    // Keep tooltip aligned if currently visible
    const tooltip = pin._vruthiTooltip;
    if (tooltip && tooltip.style.display === "block") {
      const rect = pin.getBoundingClientRect();
      tooltip.style.left = `${rect.left + rect.width / 2}px`;
      tooltip.style.top = `${rect.top}px`;
    }
  });
};

// HTML string for the click-to-open customer popup.
const buildMarkerPopupHtml = (customer) => {
  const style = MARKER_STYLES[customer.area] || MARKER_STYLES.outside;
  const coordText = customer.coords
    ? `${customer.coords[0].toFixed(5)}, ${customer.coords[1].toFixed(5)}`
    : "Not available";

  return `
    <div style="max-width:320px;font-family:inherit">
      <div style="display:flex;align-items:flex-start;gap:10px">
        <div style="flex-shrink:0">
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="${customer.area === "service" ? "6.5" : "5"}"
              stroke="#ffffff" stroke-width="1.4" fill="${style.color}" fill-opacity="0.92" />
          </svg>
        </div>
        <div style="flex:1;min-width:0">
          <div style="font-size:14px;font-weight:600;color:#111827">${customer.name || "Unnamed customer"}</div>
          <div style="font-size:12px;color:#6b7280;margin-top:2px">${customer.contact || "No contact"}</div>
          <div style="font-size:11px;color:#374151;margin-top:6px">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${style.color};margin-right:4px"></span>
            ${style.label}
          </div>
          ${customer.localBodyName ? `<div style="font-size:11px;color:#374151;margin-top:4px"><b>Local body:</b> ${customer.localBodyName} (${customer.localBodyCode})</div>` : ""}
          ${customer.wardLabel ? `<div style="font-size:11px;color:#374151"><b>${customer.wardLabel}</b></div>` : ""}
          <div style="font-size:11px;color:#6b7280;margin-top:4px;font-family:monospace">${coordText}</div>
        </div>
      </div>
    </div>
  `;
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const LocalMapAssessment = () => {
  const keralaMapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapCardRef = useRef(null);
  const pinContainerRef = useRef(null);
  const pinElementsRef = useRef([]);
  const pinBoundsRef = useRef(null);
  const cleanupCallbacksRef = useRef([]);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [coordinateOverrides, setCoordinateOverrides] = useState({});

  const [divisionIndex, setDivisionIndex] = useState([]);
  const [divisionLoading, setDivisionLoading] = useState(true);
  const [divisionError, setDivisionError] = useState(null);

  const [wardIndex, setWardIndex] = useState(null);
  const [wardLoading, setWardLoading] = useState(false);
  const [wardError, setWardError] = useState(null);

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [lookup, setLookup] = useState({});
  const [drillStatus, setDrillStatus] = useState("idle");
  const [selectedDivision, setSelectedDivision] = useState(null);

  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [pinPopup, setPinPopup] = useState(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [geocodeState, setGeocodeState] = useState({
    running: false,
    done: 0,
    total: 0,
    failed: 0,
  });

  /* ----------------------------- Data loading ----------------------------- */

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await getAllUsers("user");
        if (cancelled) return;
        if (data && data.success) {
          setUsers(data.users || []);
        } else {
          setUsersError("Could not load customers from the admin API.");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Local Map Assessment: failed to load customers", err);
          setUsersError("An error occurred while loading customers.");
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const geojson = await fetchGeoJson(LOCAL_BODIES_URL);
        if (cancelled) return;
        setDivisionIndex(buildDivisionIndex(geojson));
      } catch (err) {
        if (!cancelled) {
          console.error("Local Map Assessment: failed to load local body boundaries", err);
          setDivisionError(
            "Local body boundaries could not be loaded. Check the map data URL and your connection."
          );
        }
      } finally {
        if (!cancelled) setDivisionLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // The library destroys the Leaflet map on unmount; clean up our DOM pins.
  useEffect(
    () => () => {
      if (pinContainerRef.current) {
        pinContainerRef.current.remove();
        pinContainerRef.current = null;
      }
      pinElementsRef.current = [];
      pinBoundsRef.current = null;
      mapInstanceRef.current = null;
      if (cleanupCallbacksRef.current) {
        cleanupCallbacksRef.current.forEach((cb) => cb());
        cleanupCallbacksRef.current = [];
      }
    },
    []
  );

  /* --------------------------- Derived assessment -------------------------- */

  const customers = useMemo(
    () =>
      users.map((user) => {
        const key = getCustomerKey(user);
        const stored = resolveStoredCoords(user);
        const override = coordinateOverrides[key];
        const baseCoords = override || stored.coords;
        const coordSource = override ? "geocoded address" : stored.source;

        const coords =
          baseCoords && coordSource !== "verified location"
            ? withStableJitter(baseCoords, key)
            : baseCoords;

        const division =
          coords && divisionIndex.length > 0
            ? findDivisionForPoint(divisionIndex, coords[0], coords[1])
            : null;

        let area = "outside";
        if (division && division.code === SERVICE_AREA.code) area = "service";
        else if (division) area = "district";

        const ward =
          coords && wardIndex && wardIndex.length > 0
            ? findDivisionForPoint(wardIndex, coords[0], coords[1])
            : null;

        return {
          key,
          name: user.name,
          contact: user.contact || user.phone,
          address: user.address,
          location_string: user.location_string,
          pincode: user.pincode,
          coords,
          coordSource,
          division,
          localBodyCode: division ? division.code : null,
          localBodyName: division ? division.name : null,
          localBodyType: division ? division.type : null,
          area,
          areaLabel: MARKER_STYLES[area].label,
          wardNumber: ward ? ward.wardNumber : null,
          wardName: ward ? ward.wardName : null,
          wardLabel:
            ward && ward.wardNumber ? `Ward ${ward.wardNumber} — ${ward.wardName}` : null,
        };
      }),
    [users, coordinateOverrides, divisionIndex, wardIndex]
  );

  const stats = useMemo(() => {
    const mapped = customers.filter((customer) => customer.coords);
    return {
      total: customers.length,
      mapped: mapped.length,
      unmapped: customers.length - mapped.length,
      service: customers.filter((customer) => customer.area === "service").length,
      district: customers.filter((customer) => customer.area === "district").length,
      outside: customers.filter((customer) => customer.area === "outside").length,
    };
  }, [customers]);

  const localBodyRows = useMemo(() => {
    const counts = new Map();
    customers.forEach((customer) => {
      if (!customer.division) return;
      const current = counts.get(customer.division.code) || {
        code: customer.division.code,
        name: customer.division.name,
        type: customer.division.type,
        count: 0,
      };
      current.count += 1;
      counts.set(customer.division.code, current);
    });

    // Always surface the operating local body, even at zero customers.
    if (!counts.has(SERVICE_AREA.code)) {
      counts.set(SERVICE_AREA.code, {
        code: SERVICE_AREA.code,
        name: SERVICE_AREA.name,
        type: (lookup[SERVICE_AREA.code] || {}).lsgd_type || SERVICE_AREA.type,
        count: 0,
      });
    }

    return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [customers, lookup]);

  const wardRows = useMemo(() => {
    if (!wardIndex) return [];
    const counts = new Map();
    customers.forEach((customer) => {
      if (customer.area !== "service" || !customer.wardNumber) return;
      counts.set(customer.wardNumber, (counts.get(customer.wardNumber) || 0) + 1);
    });

    return wardIndex
      .map((ward) => ({
        number: ward.wardNumber,
        name: ward.wardName,
        count: counts.get(ward.wardNumber) || 0,
      }))
      .sort((a, b) => Number(a.number) - Number(b.number));
  }, [wardIndex, customers]);

  const mappedInServiceArea = useMemo(
    () => wardRows.reduce((total, ward) => total + ward.count, 0),
    [wardRows]
  );

  const visibleCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return customers.filter((customer) => {
      if (!customer.coords) return false;
      if (filter !== "all" && customer.area !== filter) return false;
      if (!query) return true;
      return [customer.name, customer.contact, customer.location_string, customer.address]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [customers, filter, searchQuery]);

  const unmappedCustomers = useMemo(
    () => customers.filter((customer) => !customer.coords),
    [customers]
  );

  const serviceAreaInfo = lookup[SERVICE_AREA.code] || null;

  /* ------------------------------ Map wiring ------------------------------ */

  const handleMapReady = useCallback(({ map, lookup: mapLookup }) => {
    mapInstanceRef.current = map;
    setLookup(mapLookup || {});
    setMapError(null);

    // Plain-DOM container appended to the map's marker pane. This is the
    // critical difference from the previous (buggy) approach: we never hand
    // Leaflet layer objects across the library/app copy boundary — only
    // primitive lat/lng numbers are passed to map methods.
    const pinContainer = document.createElement("div");
    pinContainer.className = PIN_CONTAINER_CLASSNAME;
    pinContainer.style.pointerEvents = "auto";
    pinContainerRef.current = pinContainer;
    pinElementsRef.current = [];
    pinBoundsRef.current = null;

    try {
      const pane = map.getPane("markerPane") || map.getContainer();
      pane.appendChild(pinContainer);

      // Reposition pins on every map move (covers pan, zoom, and drag).
      const reposition = () => {
        repositionPins(map, pinContainerRef.current, pinElementsRef.current);
      };
      map.on("move", reposition);
      // Stash unsubs for cleanup on unmount.
      cleanupCallbacksRef.current.push(() => map.off("move", reposition));
    } catch (err) {
      console.error("Local Map Assessment: could not set up the pin container", err);
    }

    setMapReady(true);
  }, []);

  const handleMapError = useCallback((error) => {
    console.error("Local Map Assessment: Kerala map error", error);
    setMapError(error && error.message ? error.message : "The Kerala map failed to load.");
  }, []);

  const focusServiceArea = useCallback(async () => {
    const instance = keralaMapRef.current;
    if (!instance) return;
    setDrillStatus("loading");
    try {
      const districtSelected = await instance.selectDistrict(SERVICE_AREA.district);
      if (!districtSelected) {
        setDrillStatus("district-missing");
        return;
      }
      const bodySelected = await instance.selectLocalBody(SERVICE_AREA.code);
      setDrillStatus(bodySelected ? "done" : "body-missing");
      setSelectedDivision(SERVICE_AREA.code);
    } catch (err) {
      console.error("Local Map Assessment: drill-down failed", err);
      setDrillStatus("error");
    }
  }, []);

  // Drive the initial drill-down as soon as the library reports the map ready.
  useEffect(() => {
    if (!mapReady || !AUTO_FOCUS_SERVICE_AREA) return;
    focusServiceArea();
  }, [mapReady, focusServiceArea]);

  const focusDivision = useCallback(async (code) => {
    const instance = keralaMapRef.current;
    if (!instance) return;
    setSelectedDivision(code);
    try {
      const selected = await instance.selectLocalBody(code);
      if (!selected) toast.error("No boundary is published for this local body.");
    } catch (err) {
      console.error("Local Map Assessment: focus failed", err);
      toast.error("Could not focus that local body on the map.");
    }
  }, []);

  const fitToCustomers = useCallback(() => {
    const map = mapInstanceRef.current;
    const bounds = pinBoundsRef.current;
    if (!map || !bounds) {
      toast.info("No mapped customers to fit yet.");
      return;
    }
    // Pass a plain [[lat,lng],[lat,lng]] array — Leaflet's fitBounds accepts
    // this directly, no Bounds object from the wrong Leaflet copy.
    const sw = bounds.minLat !== bounds.maxLat ? [bounds.minLat, bounds.minLng] : [bounds.minLat - 0.001, bounds.minLng - 0.001];
    const ne = bounds.minLat !== bounds.maxLat ? [bounds.maxLat, bounds.maxLng] : [bounds.maxLat + 0.001, bounds.maxLng + 0.001];
    try {
      map.fitBounds([sw, ne], { padding: [40, 40], maxZoom: 15 });
    } catch (err) {
      console.error("Local Map Assessment: fitBounds failed", err);
      toast.error("Could not fit the map to the customer pins.");
    }
  }, []);

  const showAllKerala = useCallback(async () => {
    const instance = keralaMapRef.current;
    if (!instance) return;
    try {
      await instance.switchToLayer("districts");
      const map = mapInstanceRef.current;
      if (map) map.setView([10.5, 76.3], 8);
    } catch (err) {
      console.error("Local Map Assessment: layer switch failed", err);
    }
  }, []);

  const toggleMapFullscreen = useCallback(async () => {
    const card = mapCardRef.current;
    if (!card) return;
    try {
      if (!document.fullscreenElement) {
        if (card.requestFullscreen) await card.requestFullscreen();
        else if (card.webkitRequestFullscreen) card.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    } catch (err) {
      console.error("Local Map Assessment: fullscreen toggle failed", err);
      toast.error("Could not open the map in fullscreen.");
    }
  }, []);

  // Track fullscreen changes + invalidate the Leaflet size so tiles/pins reflow.
  useEffect(() => {
    const onFullscreenChange = () => {
      const isFullscreen =
        Boolean(document.fullscreenElement) ||
        Boolean(document.webkitFullscreenElement);
      setIsMapFullscreen(isFullscreen);
      // Give the browser a tick to apply the fullscreen layout, then tell the
      // map to recalculate its size and reposition our DOM pins.
      setTimeout(() => {
        const map = mapInstanceRef.current;
        if (map && map.invalidateSize) {
          try {
            map.invalidateSize();
          } catch (err) {
            console.error("Local Map Assessment: invalidateSize failed", err);
          }
        }
        const container = pinContainerRef.current;
        if (map && container) {
          repositionPins(map, container, pinElementsRef.current);
        }
      }, 150);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    // Escape key fallback for browsers without fullscreenchange on Escape.
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsMapFullscreen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  /* ------------------------- Marker overlay rendering ---------------------- */

  useEffect(() => {
    const map = mapInstanceRef.current;
    const container = pinContainerRef.current;
    if (!mapReady || !map || !container) return;

    try {
      // Tear down any previous pins (DOM + tooltips)
      pinElementsRef.current.forEach((el) => {
        const tooltip = el._vruthiTooltip;
        if (tooltip && tooltip.parentNode) tooltip.remove();
      });
      pinElementsRef.current = [];
      pinBoundsRef.current = null;
      container.innerHTML = "";

      visibleCustomers.forEach((customer) => {
        if (!customer.coords) return;
        const [lat, lng] = customer.coords;

        // Track bounds (plain numbers, no Leaflet) for fitToCustomers.
        pinBoundsRef.current = extendPinBounds(pinBoundsRef.current, lat, lng);

        // --- Create a plain-DOM pin element (no Leaflet objects) ---
        const pin = document.createElement("div");
        pin.className = "vruthi-customer-pin";
        pin.style.width = `${PIN_SIZE}px`;
        pin.style.height = `${PIN_SIZE}px`;
        pin.style.pointerEvents = "auto";
        pin.style.cursor = "pointer";
        pin.style.position = "absolute";
        pin.dataset.area = customer.area;

        // Store coords on the element for repositioning (primitive numbers only)
        pin._vruthiCoords = [lat, lng];

        // --- DOM-based tooltip (NOT Leaflet bindTooltip) ---
        const tooltip = document.createElement("div");
        tooltip.className = "vruthi-customer-tooltip";
        tooltip.textContent = customer.name || "Customer";
        pin._vruthiTooltip = tooltip;
        document.body.appendChild(tooltip);

        const positionTooltip = () => {
          const rect = pin.getBoundingClientRect();
          tooltip.style.left = `${rect.left + rect.width / 2}px`;
          tooltip.style.top = `${rect.top}px`;
        };

        pin.addEventListener("mouseenter", () => {
          tooltip.style.display = "block";
          positionTooltip();
        });
        pin.addEventListener("mousemove", positionTooltip);
        pin.addEventListener("mouseleave", () => {
          tooltip.style.display = "none";
        });

        // --- DOM-based click-to-open popup (NOT Leaflet bindPopup) ---
        pin.addEventListener("click", (ev) => {
          ev.stopPropagation();
          const rect = pin.getBoundingClientRect();
          setPinPopup({
            customer,
            position: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
          });
        });

        container.appendChild(pin);
        pinElementsRef.current.push(pin);
      });

      // Initial position all pins
      repositionPins(map, container, pinElementsRef.current);
    } catch (err) {
      console.error("Local Map Assessment: could not render customer markers", err);
    }
  }, [mapReady, visibleCustomers]);

  /* ----------------------------- Ward breakdown ---------------------------- */

  const loadWardBreakdown = useCallback(async () => {
    if (wardIndex) return;
    setWardLoading(true);
    setWardError(null);
    try {
      const geojson = await fetchGeoJson(WARDS_URL);
      const maraduWards = (geojson.features || []).filter(
        (feature) =>
          String(((feature && feature.properties) || {}).sec_kerala_code) === SERVICE_AREA.code
      );
      if (maraduWards.length === 0) {
        setWardError("No ward boundaries are published for this local body.");
        return;
      }
      setWardIndex(buildDivisionIndex({ features: maraduWards }));
    } catch (err) {
      console.error("Local Map Assessment: failed to load ward boundaries", err);
      setWardError("Ward boundaries could not be loaded.");
    } finally {
      setWardLoading(false);
    }
  }, [wardIndex]);

  /* ------------------------------ Data quality ----------------------------- */

  const geocodeMissing = useCallback(async () => {
    const pending = customers.filter(
      (customer) => !customer.coords && getGeocodeCandidate(customer)
    );

    if (pending.length === 0) {
      toast.info("Every customer with an address already has coordinates.");
      return;
    }

    const cache = readGeocodeCache();
    const overrides = {};
    let done = 0;
    let failed = 0;

    setGeocodeState({ running: true, done: 0, total: pending.length, failed: 0 });

    for (const customer of pending) {
      const query = getGeocodeCandidate(customer);
      let coords = cache[query] || null;

      if (!coords) {
        coords = await geocodeAddressQuery(query);
        if (coords) cache[query] = coords;
        // Small gap to stay well inside the geocoding service rate limit.
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      if (coords) overrides[customer.key] = coords;
      else failed += 1;

      done += 1;
      setGeocodeState({ running: true, done, total: pending.length, failed });
    }

    writeGeocodeCache(cache);
    setCoordinateOverrides((previous) => ({ ...previous, ...overrides }));
    setGeocodeState({ running: false, done, total: pending.length, failed });

    if (failed === 0) {
      toast.success(`Resolved coordinates for ${done} customer${done === 1 ? "" : "s"}.`);
    } else {
      toast.warning(
        `Resolved ${done - failed} of ${done} customers. ${failed} address${
          failed === 1 ? "" : "es"
        } could not be geocoded.`
      );
    }
  }, [customers]);

  const exportAssessment = useCallback(() => {
    if (customers.length === 0) {
      toast.info("Nothing to export yet.");
      return;
    }

    try {
      const rows = customers.map((customer) => ({
        Name: customer.name || "",
        Contact: customer.contact || "",
        Address: customer.address || "",
        "Location String": customer.location_string || "",
        Latitude: customer.coords ? customer.coords[0] : "",
        Longitude: customer.coords ? customer.coords[1] : "",
        "Local Body": customer.localBodyName || "Outside Ernakulam",
        "Local Body Type": customer.localBodyType || "",
        "SEC Kerala Code": customer.localBodyCode || "",
        Ward: customer.wardNumber || "",
        "Ward Name": customer.wardName || "",
        Area: customer.areaLabel,
        "Coordinate Source": customer.coordSource || "not mapped",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Local Map Assessment");
      XLSX.writeFile(
        workbook,
        `Vruthi_LocalMapAssessment_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Local map assessment exported successfully");
    } catch (err) {
      console.error("Local Map Assessment: export failed", err);
      toast.error("Failed to export the assessment");
    }
  }, [customers]);

  /* ---------------------------------- View -------------------------------- */

  const statCards = [
    {
      title: "Mapped customers",
      value: stats.mapped,
      hint: `${stats.total} total · ${stats.unmapped} without coordinates`,
      icon: Users,
    },
    {
      title: `${SERVICE_AREA.name} (service area)`,
      value: stats.service,
      hint: `${SERVICE_AREA.code} · ${
        serviceAreaInfo && serviceAreaInfo.number_of_wards
          ? `${serviceAreaInfo.number_of_wards} wards`
          : `${SERVICE_AREA.type}`
      }`,
      icon: Target,
    },
    {
      title: "Ernakulam — other local bodies",
      value: stats.district,
      hint: "Customers outside the current service area",
      icon: Building2,
    },
    {
      title: "Outside Ernakulam",
      value: stats.outside,
      hint: "Outside the district boundary set",
      icon: MapPin,
    },
  ];

  const drillStatusLabel = {
    idle: "Waiting for the map…",
    loading: `Loading ${SERVICE_AREA.district} → ${SERVICE_AREA.name}…`,
    done: `${SERVICE_AREA.name} ${SERVICE_AREA.type} highlighted on the map`,
    "district-missing": `${SERVICE_AREA.district} district was not found in the dataset`,
    "body-missing": `No published boundary for ${SERVICE_AREA.name} (${SERVICE_AREA.code})`,
    error: "Could not highlight the service area",
  }[drillStatus];

  return (
    <div className="space-y-6">
      <style>{`
        .vruthi-customer-pins { pointer-events: none; }
        .vruthi-customer-pin {
          width: 14px; height: 14px;
          border: 1.4px solid #ffffff;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.4);
          transition: transform 0.1s ease, filter 0.1s ease;
        }
        .vruthi-customer-pin:hover {
          filter: brightness(1.15);
          transform: scale(1.25) !important;
        }
        .vruthi-customer-pin[data-area="service"] { background: #059669; }
        .vruthi-customer-pin[data-area="district"] { background: #f59e0b; }
        .vruthi-customer-pin[data-area="outside"] { background: #64748b; }
        .vruthi-customer-tooltip {
          position: fixed;
          z-index: 9999;
          background: #1f2937;
          color: #ffffff;
          font-size: 10px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 4px;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        .vruthi-customer-tooltip[style*="display: block"] { opacity: 1; }
        .vruthi-pin-popup {
          position: fixed;
          z-index: 9999;
          top: 0; left: 0;
          width: 100vw; height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.4);
          backdrop-filter: blur(2px);
        }
      `}</style>
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground text-left">Local Map Assessment</h1>
          <p className="text-muted-foreground mt-1 text-left">
            Customers plotted against Kerala&apos;s local body divisions. Service area:{" "}
            {SERVICE_AREA.name} {SERVICE_AREA.type}, {SERVICE_AREA.district}.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={focusServiceArea} disabled={!mapReady}>
            <Target className="w-4 h-4 mr-2" />
            Focus {SERVICE_AREA.name}
          </Button>
          <Button variant="outline" size="sm" onClick={showAllKerala} disabled={!mapReady}>
            <Compass className="w-4 h-4 mr-2" />
            All Kerala
          </Button>
          <Button variant="outline" size="sm" onClick={fitToCustomers} disabled={!mapReady}>
            <LocateFixed className="w-4 h-4 mr-2" />
            Fit customers
          </Button>
          <Button variant="outline" size="sm" onClick={exportAssessment}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title} className="border-border hover:shadow-md transition-smooth">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-foreground mt-2">
                    {usersLoading || divisionLoading ? "—" : card.value}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">{card.hint}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-md">
                  <card.icon className="w-6 h-6 text-primary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Map + assessment panel */}
      <div className="grid gap-6 xl:grid-cols-3">
        <Card
          ref={mapCardRef}
          className={
            isMapFullscreen
              ? "border-border overflow-hidden shadow-md bg-background flex flex-col"
              : "xl:col-span-2 border-border overflow-hidden shadow-md"
          }
        >
          <CardHeader className="border-b bg-muted/30">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPinned className="w-5 h-5 text-primary" />
                  Kerala local body divisions
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant={drillStatus === "done" ? "default" : "secondary"}>
                    {drillStatus === "loading" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    {drillStatusLabel}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleMapFullscreen}
                    title={isMapFullscreen ? "Exit fullscreen (Esc)" : "Open map in fullscreen"}
                  >
                    {isMapFullscreen ? (
                      <Minimize2 className="w-4 h-4 mr-2" />
                    ) : (
                      <Expand className="w-4 h-4 mr-2" />
                    )}
                    {isMapFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((option) => (
                  <Button
                    key={option.value}
                    size="sm"
                    variant={filter === option.value ? "default" : "outline"}
                    onClick={() => setFilter(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
                <div className="relative w-full sm:ml-auto sm:w-56">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className={isMapFullscreen ? "p-0 flex-1 min-h-0 flex flex-col" : "p-0"}>
            <div className={isMapFullscreen ? "relative flex-1 min-h-0 w-full" : "relative h-[620px] w-full"}>
              <KeralaMap
                ref={keralaMapRef}
                dataBaseUrl={KERALA_MAP_DATA_URL}
                initialLayer="districts"
                highlightColor="#059669"
                showElectionResults={false}
                onReady={handleMapReady}
                onError={handleMapError}
                className="h-full"
              />

              {/* Legend overlay */}
              <div className="absolute bottom-12 right-4 z-[1100] bg-background/95 backdrop-blur rounded-lg border border-border shadow-md p-3 space-y-2 pointer-events-none">
                <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide">
                  Customer legend
                </p>
                {Object.entries(MARKER_STYLES).map(([area, style]) => (
                  <div key={area} className="flex items-center gap-2 text-xs">
                    <span
                      className="w-3 h-3 rounded-full border border-white shadow"
                      style={{ backgroundColor: style.color }}
                    />
                    <span>{style.label}</span>
                  </div>
                ))}
                <div className="pt-1 border-t border-border/60 text-[11px] text-muted-foreground">
                  Showing {visibleCustomers.length} of {stats.mapped} mapped
                </div>
              </div>
            </div>

            {pinPopup && (
              <div
                className="vruthi-pin-popup"
                onClick={() => setPinPopup(null)}
              >
                <div
                  className="bg-background border border-border rounded-lg shadow-xl max-w-sm w-full mx-4 relative"
                  onClick={(ev) => ev.stopPropagation()}
                  dangerouslySetInnerHTML={{ __html: buildMarkerPopupHtml(pinPopup.customer) }}
                />
              </div>
            )}

            {mapError && (
              <div className="px-4 py-3 text-sm text-destructive border-t border-border">
                {mapError}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assessment panel */}
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Target className="w-5 h-5 text-primary" />
                Assessment summary
              </CardTitle>
              <CardDescription>Customer split around the operating local body</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {SERVICE_AREA.name} {SERVICE_AREA.type} · {SERVICE_AREA.code}
                </p>
                <p className="text-xs text-muted-foreground">
                  {SERVICE_AREA.district} district
                  {serviceAreaInfo && serviceAreaInfo.number_of_wards
                    ? ` · ${serviceAreaInfo.number_of_wards} wards`
                    : ""}
                </p>
              </div>

              {[
                {
                  label: "In Maradu (service area)",
                  value: stats.service,
                  color: MARKER_STYLES.service.color,
                },
                {
                  label: "Ernakulam — other local body",
                  value: stats.district,
                  color: MARKER_STYLES.district.color,
                },
                {
                  label: "Outside Ernakulam",
                  value: stats.outside,
                  color: MARKER_STYLES.outside.color,
                },
                {
                  label: "Not mapped (no coordinates)",
                  value: stats.unmapped,
                  color: "#cbd5e1",
                },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full shrink-0 border border-white shadow"
                      style={{ backgroundColor: row.color }}
                    />
                    <span className="text-sm text-muted-foreground truncate">{row.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    {usersLoading || divisionLoading ? "—" : row.value}
                  </span>
                </div>
              ))}

              <Separator />

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Maradu share of mapped customers</span>
                <span className="font-semibold text-foreground">
                  {stats.mapped > 0 ? `${Math.round((stats.service / stats.mapped) * 100)}%` : "—"}
                </span>
              </div>

              {divisionError && (
                <p className="text-xs text-destructive flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {divisionError}
                </p>
              )}

              {usersError && (
                <p className="text-xs text-destructive flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {usersError}
                </p>
              )}
            </CardContent>
          </Card>


          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5 text-primary" />
                Local body coverage
              </CardTitle>
              <CardDescription>
                {SERVICE_AREA.district} local bodies with customers. Select one to focus it on the map.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {localBodyRows.slice(0, MAX_LOCAL_BODY_ROWS).map((row) => {
                  const maxCount = localBodyRows[0] ? localBodyRows[0].count : 0;
                  const width =
                    maxCount > 0
                      ? Math.max((row.count / maxCount) * 100, row.count > 0 ? 6 : 0)
                      : 0;
                  const isServiceArea = row.code === SERVICE_AREA.code;
                  return (
                    <button
                      key={row.code}
                      onClick={() => focusDivision(row.code)}
                      className={cn(
                        "w-full text-left px-4 py-3 hover:bg-muted/50 transition-smooth",
                        selectedDivision === row.code && "bg-muted/60"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {row.name}
                          {isServiceArea && (
                            <Badge variant="outline" className="ml-2 text-[10px] align-middle">
                              Service area
                            </Badge>
                          )}
                        </span>
                        <span className="text-sm font-semibold text-muted-foreground">
                          {row.count}
                        </span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${width}%`,
                              backgroundColor: isServiceArea
                                ? MARKER_STYLES.service.color
                                : MARKER_STYLES.district.color,
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {row.code} · {row.type}
                        </span>
                      </div>
                    </button>
                  );
                })}

                {!divisionLoading && localBodyRows.length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    No customers fall inside {SERVICE_AREA.district} local bodies yet.
                  </div>
                )}
              </div>

              {localBodyRows.length > MAX_LOCAL_BODY_ROWS && (
                <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground">
                  Showing top {MAX_LOCAL_BODY_ROWS} of {localBodyRows.length} local bodies
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Layers className="w-5 h-5 text-primary" />
                {SERVICE_AREA.name} ward coverage
              </CardTitle>
              <CardDescription>
                Ward-level split inside {SERVICE_AREA.name} — useful when planning collection routes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!wardIndex && !wardLoading && !wardError && (
                <Button variant="outline" className="w-full" onClick={loadWardBreakdown}>
                  <Layers className="w-4 h-4 mr-2" />
                  Load ward breakdown
                </Button>
              )}

              {wardLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading {SERVICE_AREA.name} ward boundaries…
                </div>
              )}

              {wardError && (
                <p className="text-xs text-destructive flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {wardError}
                </p>
              )}

              {wardRows.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {mappedInServiceArea} of {stats.service} {SERVICE_AREA.name} customers classified
                    into {wardRows.filter((ward) => ward.count > 0).length} of {wardRows.length} wards.
                  </p>
                  <div className="max-h-72 overflow-y-auto divide-y divide-border rounded-lg border border-border">
                    {wardRows.map((ward) => (
                      <div
                        key={`${ward.number}-${ward.name}`}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                      >
                        <span className="text-xs text-foreground truncate">
                          <span className="font-semibold">Ward {ward.number}</span>
                          {ward.name ? ` · ${ward.name}` : ""}
                        </span>
                        <span
                          className={cn(
                            "text-xs font-semibold",
                            ward.count > 0 ? "text-foreground" : "text-muted-foreground"
                          )}
                        >
                          {ward.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Data quality */}
      <Card className="border-border">
        <CardHeader className="border-b bg-muted/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Customers missing coordinates
              </CardTitle>
              <CardDescription>
                These customers can&apos;t be placed in a local body until an address is geocoded.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{stats.unmapped} unmapped</Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={geocodeMissing}
                disabled={geocodeState.running || stats.unmapped === 0}
              >
                {geocodeState.running ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Resolve addresses
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {geocodeState.running && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Geocoding {geocodeState.done} of {geocodeState.total} customers…
                </span>
                <span>{geocodeState.failed} failed</span>
              </div>
              <Progress
                value={
                  geocodeState.total > 0
                    ? Math.round((geocodeState.done / geocodeState.total) * 100)
                    : 0
                }
              />
            </div>
          )}

          {!geocodeState.running && geocodeState.total > 0 && (
            <p className="text-xs text-muted-foreground">
              Last run: resolved {geocodeState.total - geocodeState.failed} of {geocodeState.total}{" "}
              addresses. Results are cached in this browser, so re-runs are instant.
            </p>
          )}

          {unmappedCustomers.length === 0 ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Every customer has a map location.
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {unmappedCustomers.slice(0, 9).map((customer) => (
                <div
                  key={customer.key}
                  className="rounded-lg border border-border bg-card p-3 space-y-1"
                >
                  <p className="text-sm font-medium text-foreground truncate">
                    {customer.name || "Unnamed customer"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {getGeocodeCandidate(customer) || "No address or location on file"}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{customer.contact || "—"}</p>
                </div>
              ))}
              {unmappedCustomers.length > 9 && (
                <div className="rounded-lg border border-dashed border-border p-3 flex items-center justify-center text-xs text-muted-foreground">
                  +{unmappedCustomers.length - 9} more
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LocalMapAssessment;
